import { useEffect, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { type Environment, type EnvironmentStatus, type EnvironmentStatusEntry } from "@/lib/api";
import {
	findNewChatEnvironment,
	isTransitioningEnvironmentStatus,
	loadNewChatEnvironmentData,
} from "@/components/chat/newChatEnvironment";
import { isAbortError } from "@/lib/abort-error";
import {
	readStoredNewChatEnvironmentSelection,
	writeStoredNewChatEnvironmentSelection,
} from "@/lib/new-chat-environment-selection";
import { pollEnvironmentStatusesUntil } from "@/lib/environment-lifecycle";
import { useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";

const STATUS_LABELS: Record<EnvironmentStatus, string> = {
	running: "稼働中",
	pending: "起動準備中",
	waitingForCapacity: "インスタンス確保待ち",
	shuttingDown: "終了処理中",
	errored: "異常",
	startupFailed: "起動失敗",
	notRunning: "休止中",
};

/**
 * 新規チャット下書きの対象エージェントの状態を確かめる。チャット画面は 1 つのエージェントの作業場所なので
 * エージェントは選ばせず、引き継いだエージェント (ページ更新後は前回のもの) に固定する。
 * 稼働中 (`running`) のときだけ送信先として確定し、起動途中なら稼働するまで待つ。
 */
export function NewChatEnvironmentStatus() {
	const setSelectedEnvironmentId = useChatStore((s) => s.setNewChatEnvironmentId);
	const statusRefreshVersion = useChatStore((s) => s.newChatEnvironmentStatusRefreshVersion);
	const setStatusValidating = useChatStore((s) => s.setNewChatEnvironmentStatusValidating);
	const requestStatusRefresh = useChatStore((s) => s.requestNewChatEnvironmentStatusRefresh);
	const mainView = useLayoutStore((s) => s.mainView);

	const [environment, setEnvironment] = useState<Environment | null>(null);
	const [status, setStatus] = useState<EnvironmentStatus | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [statusError, setStatusError] = useState(false);

	const pollAbortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		if (mainView !== "chats") return;
		let cancelled = false;
		pollAbortRef.current?.abort();
		pollAbortRef.current = null;
		setLoading(true);
		setError(null);
		setStatusError(false);
		setStatusValidating(true);

		const load = async () => {
			const result = await loadNewChatEnvironmentData();
			if (cancelled) return;

			if (result.projects.status !== "fulfilled" || result.environments.status !== "fulfilled") {
				setEnvironment(null);
				setStatus(null);
				useChatStore.getState().setNewChatEnvironmentId(null);
				setError("エージェントの取得に失敗しました。");
				setLoading(false);
				setStatusValidating(false);
				if (result.projects.status === "rejected") {
					console.error("新規チャットのプロジェクト取得に失敗しました", result.projects.reason);
				}
				if (result.environments.status === "rejected") {
					console.error("新規チャットの環境取得に失敗しました", result.environments.reason);
				}
				return;
			}

			// ページ更新などで対象が無いときだけ、保存済みの選択 (前回のエージェント) を引き継ぐ。
			const targetId =
				useChatStore.getState().newChatAgentId ??
				readStoredNewChatEnvironmentSelection().environmentId;
			const target = findNewChatEnvironment(
				result.projects.value,
				result.environments.value,
				targetId,
			);
			useChatStore.getState().setNewChatAgent(target);
			if (target !== null) {
				writeStoredNewChatEnvironmentSelection({
					projectId: target.projectId,
					environmentId: target.id,
				});
			}
			const statusErrorOnLoad = result.statuses.status === "rejected";
			const statusOf = (entries: readonly EnvironmentStatusEntry[]) =>
				entries.find((entry) => entry.environmentId === target?.id)?.status ?? "notRunning";
			const initialStatus =
				result.statuses.status === "fulfilled" ? statusOf(result.statuses.value) : null;
			setEnvironment(target);
			setStatus(initialStatus);
			setStatusError(statusErrorOnLoad);
			if (result.statuses.status === "rejected") {
				console.error("新規チャットの環境状態取得に失敗しました", result.statuses.reason);
			}

			setSelectedEnvironmentId(initialStatus === "running" ? (target?.id ?? null) : null);
			setLoading(false);
			setStatusValidating(false);

			if (target === null || !isTransitioningEnvironmentStatus(initialStatus ?? undefined)) return;

			const controller = new AbortController();
			pollAbortRef.current = controller;
			try {
				await pollEnvironmentStatusesUntil(
					(entries) => !isTransitioningEnvironmentStatus(statusOf(entries)),
					{
						signal: controller.signal,
						onStatuses: (entries) => {
							if (cancelled) return;
							const next = statusOf(entries);
							setStatus(next);
							if (next === "running") setSelectedEnvironmentId(target.id);
						},
					},
				);
			} catch (err: unknown) {
				// 中断は画面遷移・再読み込みによるものなので、状態を触らずに終える。
				if (isAbortError(err) || cancelled) return;
				console.error("環境状態のポーリングに失敗しました", err);
				setStatusError(true);
				setSelectedEnvironmentId(null);
			} finally {
				if (pollAbortRef.current === controller) pollAbortRef.current = null;
			}
		};

		void load();

		return () => {
			cancelled = true;
			pollAbortRef.current?.abort();
			pollAbortRef.current = null;
		};
	}, [mainView, setSelectedEnvironmentId, setStatusValidating, statusRefreshVersion]);

	if (loading) {
		return <p className="text-ui text-fg-3">エージェントの状態を確認しています…</p>;
	}
	if (error !== null) {
		return <p className="text-ui text-danger">{error}</p>;
	}
	if (environment === null) {
		return (
			<p className="text-ui text-fg-3">
				エージェント画面からエージェントを選んで、新しいチャットを始めてください。
			</p>
		);
	}
	if (statusError) {
		return (
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<p className="text-ui text-danger">
					エージェントの状態を確認できないため、チャットを始められません。
				</p>
				<ConfirmButton
					label="再試行"
					loading={loading}
					variant="secondary"
					size="sm"
					onClick={requestStatusRefresh}
					data-testid="new-chat-environment-status-retry"
				/>
			</div>
		);
	}
	if (status === "running" || status === null) return null;
	return (
		<p className="text-ui text-fg-3">
			{`${environment.name}は${STATUS_LABELS[status]}です。`}
			{isTransitioningEnvironmentStatus(status)
				? "稼働するまでお待ちください。"
				: "エージェント画面からエージェントを起動してください。"}
		</p>
	);
}
